import { useAuth0 } from "@auth0/auth0-react";
import React, { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PageLayout } from "../components/page-layout";
import { subscriptionService } from "../services/subscription.service";
import "../styles/components/pricing-page.css";
import "../styles/components/gymea-edu-dashboard.css";

type PricingPlan = {
  id: "trial" | "starter" | "pro";
  name: string;
  highlight: string;
  price: { amount: string; frequency: string };
  ctaLabel: string;
  tag?: string;
  isFeatured?: boolean;
  isDisabled?: boolean;
  // One line per row, in the same order on every card: the cards are read across
  // as much as down, so a row that exists on one plan must exist on all three.
  features: string[];
  // A perk that is not a monthly allowance, rendered outside the list so it does
  // not read as another row to compare.
  bonus?: string;
};

// Every allowance below is quoted from the "limits" map in products.json. That file
// lives in S3 and is retuned without redeploying the frontend, so these numbers do not
// follow it — change one there and change it here in the same breath, or the page sells
// a plan the usage tracker will not honour.
//
// Cards run left to right from cheapest to dearest; Pro carries the featured
// styling and the tag rather than the leading position.
const pricingPlans: PricingPlan[] = [
  {
    // The Free plan keeps the "trial" machine id: the CTA's signup redirect and
    // the active-plan mapping below both key on it.
    id: "trial",
    name: "Free",
    highlight: "No credit card, no expiry.",
    price: { amount: "$0", frequency: "per month" },
    ctaLabel: "Choose Free",
    features: [
      "3 lesson plans and 3 units of work a month",
      "Units of work up to 5 lessons",
      "4 slide decks a month, 2 with AI illustrations",
      "3 worksheets a month",
      "3 behaviour and learning support plans a month",
      "10 report comments and 10 letters home a month",
    ],
    bonus: "Plus! Get two weeks of Standard access when you sign up, on us.",
  },
  {
    id: "starter",
    name: "Standard",
    highlight: "Monthly access, no commitment.",
    price: { amount: "$15", frequency: "per month" },
    ctaLabel: "Choose Standard",
    features: [
      "8 lesson plans and 8 units of work a month",
      "Units of work up to 5 lessons",
      "10 slide decks a month, 5 with AI illustrations",
      "8 worksheets a month",
      "5 behaviour and learning support plans a month",
      "50 report comments and 30 letters home a month",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    // "Every feature", not "everything unlimited": lesson plans, units of work
    // and slide decks are all capped on Pro, and only four products are
    // genuinely unlimited.
    highlight: "Every feature, no lock-in.",
    price: { amount: "$25", frequency: "per month" },
    ctaLabel: "Upgrade to Pro",
    tag: "Best value",
    isFeatured: true,
    features: [
      "20 lesson plans and 20 units of work a month",
      "Full-term units, up to 10 lessons",
      "20 slide decks a month, 10 with AI illustrations",
      "Unlimited, differentiated worksheets",
      "Unlimited behaviour and learning support plans",
      "Unlimited report comments and letters home",
    ],
  },
];

export const PricingPage: React.FC = () => {
  const { isAuthenticated, user, loginWithRedirect } = useAuth0();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const navigate = useNavigate();

  // Which card reads as "your plan" comes from the Auth0 claim, resolved
  // against the clock, and from nowhere else. /api/usage cannot answer it: it
  // reports the *limits* a caller gets, so someone inside the sign-up fortnight
  // is indistinguishable there from someone paying for Standard. And the raw
  // claim cannot answer it either — a running trial and a promotional grant
  // both carry `status: "trialing"`, an ended trial still carries it long after
  // it ended, and a legacy record can carry `tier: "trialing"`, which matches
  // no card at all.
  const { isTrialActive, effectiveTier } = subscriptionService.getSubscriptionInfo(user);
  // The fortnight is the Free plan's trial of Standard, so it highlights the
  // Free card. A grant is Pro access and highlights Pro — collapsing it to the
  // trial offered its holder checkout on the tier they already have.
  const isSignupFortnight = isTrialActive && effectiveTier === "standard";

  const handleSignupRedirect = useCallback(async (tier?: string) => {
    if (isAuthenticated) {
      // If authenticated, navigate to profile with tier parameter for upgrade flow
      if (tier) {
        navigate(`/profile?upgrade=${tier}`);
      } else {
        navigate("/profile");
      }
      return;
    }

    // Store selected tier before redirect
    if (tier) {
      sessionStorage.setItem("selected_tier", tier);
    }

    await loginWithRedirect({
      appState: {
        returnTo: "/dashboard",
      },
      authorizationParams: {
        screen_hint: "signup",
      },
    });
  }, [isAuthenticated, loginWithRedirect, navigate]);

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-underline");
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <PageLayout className="dashboard-layout pricing-layout">
      <div className="pricing-page-container">
        <section className="pricing-hero-section">
          <div className="page-container dashboard-container">
            <div className="dashboard-header">
              <h1 ref={titleRef}>Choose your plan</h1>
              <p className="dashboard-subheading pricing-subtitle">Join the Gymea Gang.</p>
            </div>
          </div>
        </section>

        <section className="pricing-plan-section">
          <div className="pricing-content">
            <div className="pricing-plan-grid" role="list">
              {pricingPlans.map((plan) => {
                // Map plan IDs to tier values (starter -> standard, trial -> the
                // Free card). A paid card is only ever "your plan" for someone
                // actually holding that tier; everyone else keeps its CTA.
                const planTier = plan.id === "starter" ? "standard" : plan.id === "trial" ? "free" : plan.id;
                const isActivePlan =
                  isAuthenticated &&
                  (isSignupFortnight ? planTier === "free" : planTier === effectiveTier);

                return (
                  <article
                    key={plan.id}
                    className={[
                      "plan-card",
                      `plan-card--${plan.id}`,
                      plan.isFeatured ? "plan-card--featured" : "",
                      plan.isDisabled ? "plan-card--disabled" : "",
                      isActivePlan ? "plan-card--active" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    role="listitem"
                    // Each card is one entry in the plan list, so it is named by
                    // its plan rather than being read out as an unlabelled item.
                    aria-labelledby={`${plan.id}-plan-name`}
                  >
                  <header className="plan-card__header">
                    <div className="plan-card__heading">
                      <h2 className="plan-card__name" id={`${plan.id}-plan-name`}>
                        {plan.name}
                      </h2>
                      {plan.tag ? <span className="plan-card__tag">{plan.tag}</span> : null}
                    </div>
                    <p className="plan-card__highlight">{plan.highlight}</p>
                  </header>

                  <ul className="plan-card__features">
                    {plan.features.map((feature) => (
                      <li key={`${plan.id}-${feature}`}>
                        <span className="plan-card__feature-icon" aria-hidden="true">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </span>
                        <p className="plan-card__feature-text">{feature}</p>
                      </li>
                    ))}
                  </ul>

                  {plan.bonus ? (
                    <p className="plan-card__bonus">
                      <span className="plan-card__bonus-icon" aria-hidden="true">
                        🎁
                      </span>
                      {plan.bonus}
                    </p>
                  ) : null}

                  <footer className="plan-card__footer">
                    <div className="plan-card__price" aria-label={`${plan.price.amount} ${plan.price.frequency}`}>
                      <span className="plan-card__price-amount">{plan.price.amount}</span>
                      <span className="plan-card__price-frequency">{plan.price.frequency}</span>
                    </div>
                    {plan.isDisabled ? (
                      <span className="plan-card__cta-button plan-card__cta-button--disabled" aria-disabled="true">
                        {plan.ctaLabel}
                      </span>
                    ) : isActivePlan ? (
                      <button
                        type="button"
                        className="plan-card__cta-button"
                        onClick={() => navigate("/profile")}
                      >
                        Manage subscription
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="plan-card__cta-button"
                        onClick={() => handleSignupRedirect(plan.id === "trial" ? undefined : plan.id)}
                      >
                        {plan.ctaLabel}
                      </button>
                    )}
                  </footer>
                </article>
                );
              })}
            </div>

            <div className="pricing-cta" role="presentation">
              <p className="pricing-cta__title">Whole school plan</p>
              <p className="pricing-cta__description">
                Get in touch to discuss how Gymea can help your colleagues save hours on admin.
              </p>
              <a
                className="plan-card__cta-button pricing-cta__button"
                href="https://docs.google.com/forms/d/e/1FAIpQLSeb137Z79HtvYCeb59ppvQq8q9q76_AjHmd4tbi-GlUPAIGnw/viewform?usp=sharing&ouid=117310775465521436495"
                target="_blank"
                rel="noopener noreferrer"
              >
                Get in touch
              </a>
            </div>
          </div>
        </section>
      </div>
    </PageLayout>
  );
};
